import { FILTER_TYPE, PER_PAGE } from "@/constant/constants";
import React, { useCallback, useMemo, useRef } from "react";

import Actions from "./Actions";
import BaseImportModal from "@/Modals/WebDataManagement/MasterData/BaseImport/BaseImportModal";
import { CONFIRMATION_MODAL_TYPES } from "@/Modals/NewConfirmationModal/constants";
import DataTable from "@/components/NewTable";
import { cn } from "@/utils/cn";
import { statusFilters } from "@/constant/statusConstants";
import { useGet } from "@/apiHooks/HttpFunctions";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import { useSelector } from "react-redux";
import useUrlParams from "@/hooks/useUrlParams";

export default function MasterDataBaseTable({
  label,
  apiURL,
  columns = [],
  filters = [],
  useLocalizationContentKey = "localization_contents",
  hideLocalizationColumn = false,
  enableSortData = false,
  ModalComponent,
  importData,
  enableStatus = true,
  enableExport = true,
  renderLocale,
}) {
  // Hooks
  const params = useUrlParams();
  const { openModalConfirmation } = useModalConfirmation();
  const locales = useSelector(
    (state) => state.siteConfigurationReducer?.locales || []
  );

  // Refs
  const modalRef = useRef(null);
  const modalImportRef = useRef(null);

  // Derived States
  const internalFilters = useMemo(() => {
    if (!enableStatus) return [];
    const statusFiltersMap = statusFilters();
    return [
      {
        id: "filter[status]",
        label: "Status",
        type: FILTER_TYPE.SELECT,
        options: Object.entries(statusFiltersMap).map(([value, label]) => ({
          label,
          value,
        })),
      },
    ];
  }, [enableStatus]);

  const filterData = useMemo(
    () => [...internalFilters, ...filters],
    [internalFilters, filters]
  );

  // Callbacks
  const handleModalAction = useCallback(
    (type, data) => {
      openModalConfirmation({
        type,
        title: data?.name,
        id: data?.id,
        apiURL,
      });
    },
    [openModalConfirmation, apiURL]
  );

  const handleOnClickAdd = useCallback(() => modalRef.current?.open(), []);
  const handleOnClickImport = useCallback(
    () => modalImportRef.current?.open(),
    []
  );
  const handleOnClickEdit = useCallback(
    (data) => modalRef.current?.open({ id: data.id }),
    []
  );

  // Data Fetching
  const { data, isFetching, error, refetch } = useGet({
    apiURL,
    params: {
      ...params,
      perPage: params.perPage || PER_PAGE,
    },
  });

  // Merged Columns
  const mergedColumns = useMemo(() => {
    const localizationColumns = !hideLocalizationColumn
      ? locales.map((locale) => ({
          accessorKey: `${useLocalizationContentKey}.${locale.code}`,
          header: `Name (${locale.code.toUpperCase()})`,
          cell: ({ row }) =>
            renderLocale?.(
              row.original.localization_contents?.[locale.code]
            ) ?? (
              <div
                className={cn(
                  !row.original.localization_contents?.[locale.code] &&
                    "text-center"
                )}
              >
                {row.original.localization_contents?.[locale.code] ?? "-"}
              </div>
            ),
        }))
      : [];

    const actionsColumn = {
      accessorKey: "actions",
      header: "Actions",
      enableResizing: false,
      enableSorting: false,
      enablePinning: true,
      cell: ({ row }) => (
        <Actions
          row={row}
          handleOnClickDelete={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.DELETE, data)
          }
          handleOnChangeStatus={(data) =>
            handleModalAction(CONFIRMATION_MODAL_TYPES.CHANGE_STATUS, data)
          }
          handleOnClickEdit={handleOnClickEdit}
          enableStatus={enableStatus}
        />
      ),
    };

    return [actionsColumn, ...columns, ...localizationColumns];
  }, [
    hideLocalizationColumn,
    locales,
    columns,
    useLocalizationContentKey,
    handleOnClickEdit,
    enableStatus,
    handleModalAction,
  ]);

  // Render
  if (error) {
    return (
      <div>
        <p>Error loading data...</p>
        <button onClick={refetch}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <DataTable
        data={data}
        columns={mergedColumns}
        isLoading={isFetching}
        searchKey="filter[keyword]"
        exportData={
          enableExport && {
            api: apiURL,
            label: `${label} List`,
          }
        }
        importData={
          importData && {
            onClick: handleOnClickImport,
          }
        }
        addData={{
          onClick: handleOnClickAdd,
        }}
        sortData={
          enableSortData && {
            api: apiURL,
          }
        }
        filterData={filterData}
      />
      {ModalComponent && <ModalComponent ref={modalRef} />}
      {importData && (
        <BaseImportModal
          ref={modalImportRef}
          importData={importData}
          apiURL={apiURL}
        />
      )}
    </>
  );
}
